import { useHost, ref, computed, watch, onBeforeUnmount, type Ref, type ComputedRef } from "vue";
import type { SelectContentProps } from "./select.types";
import { useSharedState } from "composables/useSharedState";
import { useComponentContext } from "composables/useComponentContext";
import { useSelect, getItemLabel } from "./useSelect";
import { useKeyboardNavigation } from "composables/useKeyboardNavigation";
import type { TypeaheadItem } from "composables/useTypeahead";
import type { AcceptableValue } from "@/runtime/types/utils";
import { resolvePositioning } from "composables/usePosition";
import { resolveRenderedElement } from "@/runtime/shared/shadow-dom";
import { scrollItemIntoListboxView } from "@/runtime/shared/scroll";

export interface UseSelectContentOptions {
  props: Readonly<SelectContentProps>;
}

export interface UseSelectContentReturn {
  triggerId: Ref<string>;
  triggerWidth: Ref<number>;
  listboxStyle: ComputedRef<{ minWidth: string; maxHeight: string }>;
  positioning: ComputedRef<{
    placement: string;
    offset: number;
    boundaryMargin: number;
    collisionStrategy: string;
  }>;
  select: ReturnType<typeof useSelect>;
  handleFloatingClose: () => void;
  handleFloatingOpen: () => void;
}

/** Locate the trigger element and generate a trigger ID. */
const resolveTrigger = (
  host: Element | null,
  uid: number,
): { triggerIdValue: string; triggerElement: HTMLElement | null; selectElement: Element | null } => {
  let triggerIdValue = `e-select-trigger-${uid}`;
  let triggerElement: HTMLElement | null = null;
  let selectElement: Element | null = null;

  if (host) {
    selectElement = host.closest("e-select");
    if (selectElement) {
      const trigger = selectElement.querySelector("e-select-trigger") as HTMLElement | null;
      if (trigger) {
        triggerElement = trigger;
        if (selectElement.id) {
          triggerIdValue = `${selectElement.id}-trigger`;
        }
        trigger.id = triggerIdValue;
      }
    }
  }

  return { triggerIdValue, triggerElement, selectElement };
};

/** Get all enabled item values from the DOM. */
const getEnabledItemValues = (selectElement: Element | null): AcceptableValue[] => {
  if (!selectElement) return [];
  const items = selectElement.querySelectorAll("e-select-item:not([disabled])");
  const values: AcceptableValue[] = [];
  for (const item of items) {
    const value = (item as any).value;
    if (value !== undefined) values.push(value);
  }
  return values;
};

/**
 * Scroll an item into view by its value, finding the listbox-content scroll
 * container by walking parent/host chain (crosses shadow boundaries).
 */
const scrollItemIntoView = (selectElement: Element | null, value: AcceptableValue): void => {
  if (value === undefined || !selectElement) return;
  const items = selectElement.querySelectorAll("e-select-item");
  for (const item of items) {
    if ((item as any).value === value) {
      scrollItemIntoListboxView(item as HTMLElement);
      break;
    }
  }
};

/** Build typeahead items from the DOM. */
const buildTypeaheadItems = (selectElement: Element | null): TypeaheadItem[] => {
  if (!selectElement) return [];
  const items = selectElement.querySelectorAll("e-select-item:not([disabled])");
  const result: TypeaheadItem[] = [];
  for (const item of items) {
    const value = (item as any).value;
    if (value !== undefined) {
      const label = getItemLabel(selectElement, value) ?? String(value);
      result.push({ value, label });
    }
  }
  return result;
};

/** Resolve which value to highlight when the floating panel opens. */
const resolveOpenHighlight = (
  currentValue: AcceptableValue | AcceptableValue[] | undefined,
  enabledValues: AcceptableValue[],
): AcceptableValue | undefined => {
  let target: AcceptableValue | undefined;
  if (currentValue !== undefined) {
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      target = currentValue[0];
    } else if (!Array.isArray(currentValue)) {
      target = currentValue;
    }
  }
  if (target === undefined && enabledValues.length > 0) {
    target = enabledValues[0];
  }
  return target;
};

/** Wire keyboard navigation, document listener, and lifecycle cleanup. */
const setupKeyboardNavigation = (select: ReturnType<typeof useSelect>, selectElement: Element | null): void => {
  const { handleKeydown: navKeydown, resetTypeahead } = useKeyboardNavigation({
    getItemValues: () => getEnabledItemValues(selectElement),
    getHighlighted: () => select.highlightedValue.value,
    setHighlighted: (v) => select.setHighlightedValue(v),
    scrollIntoView: (v) => scrollItemIntoView(selectElement, v),
    onSelect: (v) => select.toggleSelection(v),
    selectKeys: ["Enter", " "],
    onEscape: () => select.close(),
    onTab: () => select.close(),
    typeahead: {
      getItems: () => buildTypeaheadItems(selectElement),
      getCycleFrom: () => select.highlightedValue.value ?? select.modelValue.value,
    },
  });

  const handleKeydown = (event: KeyboardEvent) => {
    if (!select.isOpen.value) return;
    navKeydown(event);
    if (event.defaultPrevented) event.stopPropagation();
  };

  watch(
    () => select.isOpen.value,
    (isOpen) => {
      if (isOpen) {
        document.addEventListener("keydown", handleKeydown, true);
      } else {
        document.removeEventListener("keydown", handleKeydown, true);
        resetTypeahead();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleKeydown, true);
    resetTypeahead();
  });
};

export const useSelectContent = (options: UseSelectContentOptions): UseSelectContentReturn => {
  const { props } = options;
  useSharedState(props);

  const select = useSelect();
  const host = useHost();
  const context = useComponentContext();

  const { triggerIdValue, triggerElement, selectElement } = resolveTrigger(host, context.uid!);
  const triggerId = ref(triggerIdValue);
  const triggerWidth = ref(triggerElement ? resolveRenderedElement(triggerElement).offsetWidth : 160);

  const listboxStyle = computed(() => ({
    minWidth: `${triggerWidth.value}px`,
    maxHeight: props.maxHeight ?? "300px",
  }));
  const positioning = computed(() => resolvePositioning(select.selectProps.value?.positioning, props));

  setupKeyboardNavigation(select, selectElement);

  const handleFloatingClose = () => {
    select.setHighlightedValue(undefined);
    select.setFocusValue(undefined);
    triggerElement?.focus();
    select.close();
  };

  const handleFloatingOpen = () => {
    if (triggerElement) {
      triggerWidth.value = resolveRenderedElement(triggerElement).offsetWidth;
    }
    const valueToHighlight = resolveOpenHighlight(select.modelValue.value, getEnabledItemValues(selectElement));
    if (valueToHighlight !== undefined) {
      select.setHighlightedValue(valueToHighlight);
    }
  };

  return { triggerId, triggerWidth, listboxStyle, positioning, select, handleFloatingClose, handleFloatingOpen };
};
